import Text from 'antd/lib/typography/Text';
import React from 'react';

import { toCurrency } from 'utils/utils';
import DataGrid from 'components/DataGrid';
import { DataGridColDef, DataGridProps } from 'components/DataGrid/DataGrid';
import { FormInstance, InputNumber, notification } from 'antd';
import InputCurrency from 'components/InputCurrency';
import FormInput from 'components/FormInput';
import { sumBy } from 'lodash';

export type AssetsTableProps<D = any> = Omit<DataGridProps<D>, 'columns'> & {
  mfcolumn?: boolean;
  form?: FormInstance;
  currcode?: string;
  handleLineChange?: (assetid: number, assetQty: number) => void;
  status?: string;
  selectedRowKeys?: any[];
};

const AssetsTable = (props: AssetsTableProps) => {

  const updateAmount = (
    index: number,
    assetqty: number,
    unitnetamount: number,
    qtyavailable: number,
    assetid: number
  ) => {
    if (assetqty > qtyavailable) {
      notification.error({ message: 'Qty to reduce...' });
      return;
    }

    let assets: any[] = props.form?.getFieldValue('mf');
    assets.forEach((a: any, bindex: number) => {
      if (bindex === index) {
        a.mfAssetNetAmount = unitnetamount * assetqty;
        a.mfassetqty = assetqty;
        // a.assetid = assetid;
      }
    });
    props.form?.setFieldsValue({
      mf: assets.map((asset, i) =>
        index === i
          ? {
              ...asset,
              // assetid: asset.assetid,
              assetNetAmount: asset.mfAssetNetAmount,
              mfAssetNetAmount: asset.mfAssetNetAmount,
              mfassetqty: asset.mfassetqty,
            }
          : { ...asset }
      ),
      amountToUse: sumBy(assets, 'mfAssetNetAmount'),
      selectedAssets: assets.map((asset, i) =>
        index === i
          ? {
              ...asset,
              // assetid: asset.assetid,
              assetNetAmount: asset.mfAssetNetAmount,
              mfAssetNetAmount: asset.mfAssetNetAmount,
              mfassetqty: asset.mfassetqty,
            }
          : { ...asset }
      ),
    });
    props.handleLineChange!(assetid, assetqty);
  };

  const columns: DataGridColDef<any>[] = [
    {
      comcode: 'Asset.assetreference',
      dataIndex: 'assetreference',
      width: 100,
      fixed: 'left',
    },
    {
      comcode: 'Asset.assetdescription',
      dataIndex: 'assetlabel',
      width: 180,
    },
    {
      comcode: 'Asset.assetstatuslabel',
      dataIndex: 'statuslabel',
      width: 100,
      render: (statuslabel, record) =>
        record.assetstatus !== props.status ? (
          <Text type="warning">{statuslabel}</Text>
        ) : (
          <Text style={{ color: 'green' }}>{statuslabel}</Text>
        ),
    },
    {
      comcode: 'Asset.assetorderreference',
      dataIndex: 'assetorderreference',
      width: 100,
    },
    {
      comcode: 'Asset.assetdeliveryreference',
      dataIndex: 'assetdeliveryreference',
      width: 100,
    },
    {
      comcode: 'Asset.assetdeliverydate',
      dataIndex: 'assetdeliverydate',
      width: 100,
    },
    {
      comcode: 'Asset.suppliername',
      dataIndex: 'suppliername',
      width: 180,
    },
    {
      align: 'right',
      comcode: 'Asset.assetnetamount',
      dataIndex: 'assetnetamount',
      fixed: 'right',
      render: (value) =>
        toCurrency(
          value,
          props?.currcode ?? localStorage.getItem('currcode') ?? ''
        ),
      width: 120,
    },
    {
      align: 'right',
      comcode: 'Asset.assetfinamount',
      dataIndex: 'assetfinamount',
      fixed: 'right',
      render: (value) =>
        toCurrency(
          value,
          props?.currcode ?? localStorage.getItem('currcode') ?? ''
        ),
      width: 120,
    },
    {
      comcode: 'Asset.assetrentalpart',
      dataIndex: 'rentalpart',
      render: (value) =>
        toCurrency(
          value,
          props?.currcode ?? localStorage.getItem('currcode') ?? ''
        ),
      fixed: 'right',
      width: 100,
      align: 'right',
    },
    {
      comcode: 'Asset.assetrvamount',
      dataIndex: 'assetrvamount',
      render: (value) =>
        toCurrency(
          value,
          props?.currcode ?? localStorage.getItem('currcode') ?? ''
        ),
      fixed: 'right',
      width: 100,
      align: 'right',
    },

    {
      align: 'right',
      comcode: 'Asset.assetqty',
      dataIndex: 'assetqty',
      fixed: 'right',
      width: 120,
    },

    {
      align: 'right',
      comcode: 'Asset.assetqtyavailable',
      dataIndex: 'assetqtyavailable',
      fixed: 'right',
      width: 120,
    },
    {
      comcode: 'Asset.assetunitnetamount',
      dataIndex: 'assetunitnetamount',
      render: (value) =>
        toCurrency(
          value,
          props?.currcode ?? localStorage.getItem('currcode') ?? ''
        ),
      fixed: 'right',
      width: 100,
      align: 'right',
    },
    {
      comcode: 'Asset.mfassetqty',
      dataIndex: 'mfassetqty',
      render: (val, record, index) => (
        <FormInput name={['mf', index, 'mfassetqty']} noStyle>
          <InputNumber
            defaultValue={record.assetqtyavailable}
            max={record.assetqtyavailable}
            min={0}
            onChange={(e) =>
              updateAmount(
                index,
                e,
                record.assetunitnetamount,
                record.assetqtyavailable,
                record.assetid
              )
            }
          />
        </FormInput>
      ),
      fixed: 'right',
      width: 150,
      align: 'right',
    },

    {
      comcode: 'Asset.mfAssetNetAmount',
      dataIndex: 'mfAssetNetAmount',
      render: (val, record, index) => (
        <InputCurrency
          currcode={props?.currcode ?? localStorage.getItem('currcode') ?? ''}
          comcode="mfAssetNetAmount"
          fieldname={['mf', index, 'mfAssetNetAmount']}
          defaultValue={val}
          noStyle
        />
      ),
      fixed: 'right',
      width: 150,
      align: 'right',
    },
  ];
  return (
    <DataGrid
      columns={
        !props.mfcolumn
          ? columns.filter(
              (el) =>
                el.dataIndex !== 'mfAssetNetAmount' &&
                el.dataIndex !== 'mfassetqty' &&
                el.dataIndex !== 'assetqtyavailable'
            )
          : columns
      }
      {...props}
    />
  );
};

export default AssetsTable;
